import React from 'react';
import { MetricKey } from '../types/StockData';

interface MetricSelectorProps {
  selectedMetrics: MetricKey[];
  onMetricChange: (metrics: MetricKey[]) => void;
  windowSize: number;
}

const MetricSelector: React.FC<MetricSelectorProps> = ({ selectedMetrics, onMetricChange, windowSize }) => {
  const baseMetrics: { key: MetricKey; label: string; description: string }[] = [
    { key: 'callVol', label: 'Call Volume', description: 'Total call options volume' },
    { key: 'putVol', label: 'Put Volume', description: 'Total put options volume' },
    { key: 'stockVol', label: 'Stock Volume', description: 'Underlying stock volume' },
    { key: 'nope', label: 'NOPE', description: 'Net Options Pricing Effect' },
    { key: 'nopeFill', label: 'NOPE Fill', description: 'NOPE Fill indicator' },
    { key: 'callDelta', label: 'Call Delta', description: 'Total call delta exposure' },
    { key: 'putDelta', label: 'Put Delta', description: 'Total put delta exposure' },
    { key: 'callFillDelta', label: 'Call Fill Delta', description: 'Call fill delta' },
    { key: 'putFillDelta', label: 'Put Fill Delta', description: 'Put fill delta' },
    { key: 'transactions', label: 'Transactions', description: 'Number of transactions' },
    { key: 'dirDeltaFlow', label: 'Dir Delta Flow', description: 'Directional delta flow' },
    { key: 'dirVegaFlow', label: 'Dir Vega Flow', description: 'Directional vega flow' },
    { key: 'totalDeltaFlow', label: 'Total Delta Flow', description: 'Total delta flow' },
    { key: 'totalVegaFlow', label: 'Total Vega Flow', description: 'Total vega flow' },
    { key: 'deltaFlowPerTransaction', label: 'Delta Flow/Txn', description: 'Delta flow per transaction' },
    { key: 'vegaFlowPerTransaction', label: 'Vega Flow/Txn', description: 'Vega flow per transaction' },
    { key: 'deltaFlowConcentration', label: 'Delta Flow Conc.', description: 'Delta flow concentration' },
    { key: 'vegaFlowConcentration', label: 'Vega Flow Conc.', description: 'Vega flow concentration' },
    { key: 'otmDeltaRatio', label: 'OTM Delta Ratio', description: 'Out-of-the-money delta ratio' },
    { key: 'otmVegaRatio', label: 'OTM Vega Ratio', description: 'Out-of-the-money vega ratio' },
    { key: 'speculationScore', label: 'Speculation Score', description: 'Speculation activity score' },
    { key: 'institutionalScore', label: 'Institutional Score', description: 'Institutional activity score' },
    { key: 'netGamma', label: 'Net Gamma', description: 'Net gamma exposure' },
    { key: 'netCharm', label: 'Net Charm', description: 'Net charm exposure' },
    { key: 'netVanna', label: 'Net Vanna', description: 'Net vanna exposure' },
    { key: 'netDir', label: 'Net Dir', description: 'Net directional exposure' },
    { key: 'netOi', label: 'Net OI', description: 'Net open interest' },
    { key: 'netVol', label: 'Net Vol', description: 'Net volatility exposure' },
    
    // Options Flow Metrics
    { key: 'callBidAskRatio', label: 'Call Bid/Ask Ratio', description: 'Call volume bid side / ask side ratio' },
    { key: 'putBidAskRatio', label: 'Put Bid/Ask Ratio', description: 'Put volume bid side / ask side ratio' },
    { key: 'callPutVolumeRatio', label: 'Call/Put Volume Ratio', description: 'Call volume / put volume ratio' },
    { key: 'optionsCallVolume', label: 'Options Call Volume', description: 'Options flow call volume' },
    { key: 'optionsPutVolume', label: 'Options Put Volume', description: 'Options flow put volume' },
    { key: 'optionsTotalVolume', label: 'Options Total Volume', description: 'Total options flow volume' },
    { key: 'optionsTotalPremium', label: 'Options Total Premium', description: 'Total options premium' },
    { key: 'callBidVolume', label: 'Call Bid Volume', description: 'Call volume on bid side' },
    { key: 'callAskVolume', label: 'Call Ask Volume', description: 'Call volume on ask side' },
    { key: 'putBidVolume', label: 'Put Bid Volume', description: 'Put volume on bid side' },
    { key: 'putAskVolume', label: 'Put Ask Volume', description: 'Put volume on ask side' }
  ];

  // Additional metrics available only for 5+ minute windows
  const conditionalMetrics: { key: MetricKey; label: string; description: string }[] = [
    { key: 'volume', label: 'Volume', description: 'Trading volume (5+ min windows only)' },
    { key: 'ldpm', label: 'LDPM', description: 'Liquidity Driven Price Movement (5+ min windows only)' }
  ];

  // Combine metrics based on window size
  const metrics = windowSize >= 5 ? [...baseMetrics, ...conditionalMetrics] : baseMetrics;

  const handleMetricToggle = (metricKey: MetricKey) => {
    const isSelected = selectedMetrics.includes(metricKey);
    
    if (isSelected) {
      // Remove metric if already selected
      const newMetrics = selectedMetrics.filter(m => m !== metricKey);
      onMetricChange(newMetrics);
    } else if (selectedMetrics.length < 2) {
      // Add metric if less than 2 are selected
      const newMetrics = [...selectedMetrics, metricKey];
      onMetricChange(newMetrics);
    }
    // Do nothing if trying to add a third metric
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Select Metrics to Compare</h3>
        <div className="text-sm text-gray-500">
          {selectedMetrics.length}/2 selected
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {metrics.map((metric) => {
          const isSelected = selectedMetrics.includes(metric.key);
          const canSelect = selectedMetrics.length < 2 || isSelected;
          
          return (
            <button
              key={metric.key}
              onClick={() => handleMetricToggle(metric.key)}
              disabled={!canSelect}
              className={`p-3 text-sm rounded-lg border transition-all duration-200 text-left ${
                isSelected
                  ? 'bg-blue-500 text-white border-blue-500 shadow-md'
                  : canSelect
                  ? 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                  : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
              }`}
              title={metric.description}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="font-medium">{metric.label}</div>
                {isSelected && (
                  <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  </div>
                )}
              </div>
              <div className={`text-xs ${
                isSelected ? 'text-blue-100' : canSelect ? 'text-gray-500' : 'text-gray-400'
              }`}>
                {metric.description}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MetricSelector;
